/**
 * BancoPropuestasApiClient -- la conciliacion del banco que propone Argemiro, dia por dia, y el SI del dueno.
 *
 * BAN-BANDEJA-PROPUESTAS-T1 (Ares #28116). Fuente: nexo-api Controllers/BancoPropuestasController.cs y
 * Models/Dtos/BancoPropuestasDtos.cs (origin/main, 15-oct):
 *   GET  /api/v1/banco/propuestas?businessId=&desde=&hasta=   (yyyy-MM-dd; sin fechas = el mes en curso; tope 62 dias)
 *   POST /api/v1/banco/propuestas/decidir  {businessId, fecha, decision, bankMovementId?, nota?}        solo administrador
 *   POST /api/v1/banco/propuestas/egreso   {businessId, bankMovementId, decision, marcaPrivada?, concepto?, nota?}
 *
 * EL CUERPO SE ARMA AQUI y en camelCase (ASP.NET no quita guiones bajos: un snake_case llega vacio sin error).
 * Nunca lanza: devuelve {ok:false, motivo, detalle} con la causa, para que la pantalla diga la verdad.
 *
 * @module infrastructure/api/BancoPropuestasApiClient
 */

import { ApiBase } from './ApiBase.js';

const BASE = '/api/v1/banco/propuestas';

/** Lo que el dueno puede responder (el DTO acepta exactamente estas tres). */
export const DECISIONES = { ACEPTAR: 'aceptar', RECHAZAR: 'rechazar', REVISAR: 'revisar' };

/** La causa del fallo, por el estado HTTP. */
export function motivoDelFallo(res) {
  const code = res?.code;
  // Este controlador dice el motivo en {error: '...'}; ApiBase solo lee mensaje/message/detail, asi que se toma del cuerpo.
  const cuerpo = res?.details && typeof res.details === 'object' ? res.details : {};
  const detalle = (typeof cuerpo.error === 'string' && cuerpo.error)
    || (typeof res?.error === 'string' ? res.error : res?.error?.message) || '';
  if (code === 401 || code === 'UNAUTHORIZED') return { motivo: 'SIN_SESION', detalle: 'la sesion se cerro' };
  if (code === 403) return { motivo: 'PROHIBIDO', detalle: 'solo el administrador decide la conciliacion' };
  if (code === 404 || code === 405) return { motivo: 'NO_PUBLICADO', detalle: 'la API todavia no tiene esta ruta' };
  if (code === 400) return { motivo: 'DATOS', detalle: detalle || 'la API no acepto los datos' };
  if (code === 'NETWORK' || code === 'TIMEOUT') return { motivo: 'SIN_RED', detalle: 'no pudimos hablar con la API' };
  return { motivo: 'ERROR_API', detalle: detalle || 'la API respondio con error' };
}

function falla(res) {
  return { ok: false, ...motivoDelFallo(res) };
}

export const BancoPropuestasApiClient = {
  /** Las propuestas del rango: {ok, data:{businessId, desde, hasta, mes, dias}} sin normalizar (eso es de la aplicacion). */
  async propuestas({ businessId, desde, hasta } = {}) {
    if (!businessId) return { ok: false, motivo: 'DATOS', detalle: 'falta el negocio' };
    const qs = new URLSearchParams({ businessId });
    if (desde) qs.set('desde', desde);
    if (hasta) qs.set('hasta', hasta);
    const res = await ApiBase.get(`${BASE}?${qs}`);
    if (!res.ok) return falla(res);
    return { ok: true, data: res.data?.data ?? res.data ?? {} };
  },

  /** El SI (o no) del dueno sobre un dia completo, o sobre un movimiento del dia si trae bankMovementId. */
  async decidirDia({ businessId, fecha, decision, bankMovementId, nota } = {}) {
    if (!businessId || !fecha || !decision) return { ok: false, motivo: 'DATOS', detalle: 'la propuesta llego incompleta' };
    const body = { businessId, fecha, decision };
    if (bankMovementId) body.bankMovementId = bankMovementId;
    if (nota) body.nota = nota;
    const res = await ApiBase.post(`${BASE}/decidir`, body);
    if (!res.ok) return falla(res);
    return { ok: true, data: res.data?.data ?? res.data ?? {} };
  },

  /** El egreso de una salida del banco sin gasto: aceptar (con marca gerencial/personal o ninguna), rechazar o revisar. */
  async decidirEgreso({ businessId, bankMovementId, decision, marcaPrivada, concepto, nota } = {}) {
    if (!businessId || !bankMovementId || !decision) return { ok: false, motivo: 'DATOS', detalle: 'la propuesta llego incompleta' };
    const body = { businessId, bankMovementId, decision };
    if (marcaPrivada) body.marcaPrivada = marcaPrivada;
    if (concepto) body.concepto = concepto;
    if (nota) body.nota = nota;
    const res = await ApiBase.post(`${BASE}/egreso`, body);
    if (!res.ok) return falla(res);
    return { ok: true, data: res.data?.data ?? res.data ?? {} };
  }
};
